import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ChevronDown, ChevronUp, ChevronsUpDown, Filter } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

const Investments = () => {
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [filteredInvestments, setFilteredInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState(['all']);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [columnView, setColumnView] = useState('default');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Column configurations for different views
  const columnConfigs = {
    default: {
      label: 'Default View (11 cols)',
      columns: [
        { key: 'Investment ID', label: 'Investment ID' },
        { key: 'Status', label: 'Status' },
        { key: 'Term Percentage', label: '% of Term' },
        { key: '% RTR Paid', label: '% RTR Paid' },
        { key: 'Average Monthly Receivables', label: 'Avg Monthly Remit' },
        { key: 'Commission (Points)', label: 'Commission' },
        { key: 'Criteria Set', label: 'Criteria Set' },
        { key: 'Days Since Last Payment', label: 'Days Outstanding' },
        { key: 'Est. # Pmts', label: 'Est. # Pmts' },
        { key: 'Factor Rate', label: 'Factor Rate' },
        { key: 'FICO Range', label: 'FICO Range' },
      ]
    },
    all: {
      label: 'All Columns (33 cols)',
      columns: [
        { key: 'Investment ID', label: 'Investment ID' },
        { key: 'Status', label: 'Status' },
        { key: 'Purchase Price', label: 'Purchase Price' },
        { key: 'Net RTR', label: 'Net RTR' },
        { key: 'Gross RTR', label: 'Gross RTR' },
        { key: 'Net Received', label: 'Net Received' },
        { key: 'Net Profit', label: 'Net Profit' },
        { key: 'Invested Amt', label: 'Invested Amt' },
        { key: 'Term Percentage', label: '% of Term' },
        { key: '% RTR Paid', label: '% RTR Paid' },
        { key: 'Average Monthly Receivables', label: 'Avg Monthly Remit' },
        { key: 'Commission (Points)', label: 'Commission' },
        { key: 'Criteria Set', label: 'Criteria Set' },
        { key: 'Days Since Last Payment', label: 'Days Outstanding' },
        { key: 'Est. # Pmts', label: 'Est. # Pmts' },
        { key: 'Factor Rate', label: 'Factor Rate' },
        { key: 'FICO Range', label: 'FICO Range' },
        { key: 'Term (mo)', label: 'Term (mo)' },
        { key: 'Lien Position', label: 'Lien Position' },
        { key: 'Industry', label: 'Industry' },
        { key: 'Payment Frequency', label: 'Payment Frequency' },
        { key: 'Funded Date', label: 'Funded Date' },
        { key: 'Last Payment Date', label: 'Last Payment' },
        { key: 'Mgmt Fee %', label: 'Mgmt Fee %' },
        { key: 'Your Participation %', label: 'Your Participation %' },
        { key: 'Funder Participation %', label: 'Funder Participation %' },
        { key: 'Funder Participation $', label: 'Funder Participation $' },
        { key: 'Total Merchant Funding Amount', label: 'Total Deal Size' },
        { key: 'Time In Business', label: 'Time In Business' },
        { key: 'Incorporation Date', label: 'Incorporation Date' },
      ]
    },
    financial: {
      label: 'Financial Only (14 cols)',
      columns: [
        { key: 'Investment ID', label: 'Investment ID' },
        { key: 'Status', label: 'Status' },
        { key: 'Purchase Price', label: 'Purchase Price' },
        { key: 'Invested Amt', label: 'Invested Amt' },
        { key: 'Net RTR', label: 'Net RTR' },
        { key: 'Gross RTR', label: 'Gross RTR' },
        { key: 'Net Received', label: 'Net Received' },
        { key: 'Net Profit', label: 'Net Profit' },
        { key: 'Mgmt Fee %', label: 'Mgmt Fee %' },
        { key: 'Commission (Points)', label: 'Commission' },
        { key: 'Factor Rate', label: 'Factor Rate' },
        { key: 'Your Participation %', label: 'Your Participation %' },
        { key: 'Funder Participation %', label: 'Funder Participation %' },
        { key: 'Total Merchant Funding Amount', label: 'Total Deal Size' },
      ]
    },
    performance: {
      label: 'Performance Only (13 cols)',
      columns: [
        { key: 'Investment ID', label: 'Investment ID' },
        { key: 'Status', label: 'Status' },
        { key: 'Term Percentage', label: '% of Term' },
        { key: '% RTR Paid', label: '% RTR Paid' },
        { key: 'Days Since Last Payment', label: 'Days Outstanding' },
        { key: 'Last Payment Date', label: 'Last Payment' },
        { key: 'Average Monthly Receivables', label: 'Avg Monthly Remit' },
        { key: 'Net Profit', label: 'Net Profit' },
        { key: 'Net Received', label: 'Net Received' },
        { key: 'Term (mo)', label: 'Term (mo)' },
        { key: 'Est. # Pmts', label: 'Est. # Pmts' },
        { key: 'Payment Frequency', label: 'Payment Frequency' },
        { key: 'Funded Date', label: 'Funded Date' },
      ]
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Select All' },
    { value: 'Matched', label: 'Matched' },
    { value: 'Pending Funding', label: 'Pending Funding' },
    { value: 'Funded', label: 'Funded' },
    { value: 'Paid In Full', label: 'Paid In Full' },
    { value: 'Current', label: 'Current' },
    { value: 'Late 31-60 Days', label: 'Late 31-60 Days' },
    { value: 'Late 61-90 Days', label: 'Late 61-90 Days' },
    { value: 'Late 91-120 Days', label: 'Late 91-120 Days' },
    { value: 'Late 121+ Days', label: 'Late 121+ Days' },
    { value: 'Write Off', label: 'Write Off' },
    { value: 'Settled', label: 'Settled' },
    { value: 'Reversed', label: 'Reversed' },
    { value: 'Canceled', label: 'Canceled' },
  ];

  useEffect(() => {
    fetchInvestments();
  }, []);

  useEffect(() => {
    filterInvestments();
    setCurrentPage(1); // Reset to first page when filters change
  }, [investments, selectedStatuses, statusFilter]);

  const fetchInvestments = async () => {
    try {
      const response = await api.get('/investments');
      setInvestments(response.data);
    } catch (error) {
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const filterInvestments = () => {
    let filtered = [...investments];

    // Filter by status filter pills OR dropdown
    if (statusFilter !== 'all') {
      filtered = filtered.filter((inv) => 
        inv['Status'] === statusFilter
      );
    } else if (!selectedStatuses.includes('all')) {
      filtered = filtered.filter((inv) => 
        selectedStatuses.includes(inv['Status'])
      );
    }

    setFilteredInvestments(filtered);
  };

  // Sort function
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Get sorted data
  const getSortedInvestments = () => {
    if (!sortConfig.key) return filteredInvestments;

    return [...filteredInvestments].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;

      // Try to parse as number
      const aNum = parseFloat(String(aVal).replace(/[$,%]/g, ''));
      const bNum = parseFloat(String(bVal).replace(/[$,%]/g, ''));

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  };

  const sortedInvestments = getSortedInvestments();

  const toggleStatus = (status) => {
    // Clear the dropdown when using pills
    if (statusFilter !== 'all') {
      setStatusFilter('all');
    }
    
    if (status === 'all') {
      setSelectedStatuses(['all']);
    } else {
      let newStatuses = selectedStatuses.filter(s => s !== 'all');
      if (newStatuses.includes(status)) {
        newStatuses = newStatuses.filter(s => s !== status);
      } else {
        newStatuses.push(status);
      }
      setSelectedStatuses(newStatuses.length === 0 ? ['all'] : newStatuses);
    }
  };

  const handleStatusDropdownChange = (value) => {
    setStatusFilter(value);
    // Clear pills when using dropdown
    if (value !== 'all') {
      setSelectedStatuses(['all']);
    }
  };

  const downloadInvestments = () => {
    const headers = [
      'Investment ID', '% of Term', '% RTR Paid', 'Average Monthly Receivables',
      'Commission', 'Criteria Set', 'Days Since Last Payment', 'Est. # Pmts',
      'Factor Rate', 'FICO Range', 'Status', 'Purchase Price', 'Net RTR'
    ];
    
    const rows = filteredInvestments.map((inv) => [
      inv['Investment ID'] || '',
      inv['Term Percentage'] || '',
      inv['% RTR Paid'] || '',
      inv['Average Monthly Receivables'] || '',
      inv['Commission (Points)'] || '',
      inv['Criteria Set'] || '',
      inv['Days Since Last Payment'] || '',
      inv['Est. # Pmts'] || '',
      inv['Factor Rate'] || '',
      inv['FICO Range'] || '',
      inv['Status'] || '',
      inv['Purchase Price'] || '',
      inv['Net RTR'] || ''
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map((row) => row.join(',')).join('\n');

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', 'investments.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Investments exported to CSV');
  };

  // Calculate totals
  const calculateTotals = () => {
    if (filteredInvestments.length === 0) return {};
    
    const parsePercent = (val) => {
      if (!val) return 0;
      return parseFloat(val.toString().replace('%', ''));
    };
    
    const totalTermPercent = filteredInvestments.reduce((sum, inv) => 
      sum + parsePercent(inv['Term Percentage']), 0
    );
    const avgTermPercent = totalTermPercent / filteredInvestments.length;
    
    const totalRTRPaid = filteredInvestments.reduce((sum, inv) => 
      sum + parsePercent(inv['% RTR Paid']), 0
    );
    const avgRTRPaid = totalRTRPaid / filteredInvestments.length;
    
    const parseCommission = (val) => {
      if (!val) return 0;
      const match = val.toString().match(/(\d+(\.\d+)?)/);
      return match ? parseFloat(match[1]) : 0;
    };
    
    const totalCommission = filteredInvestments.reduce((sum, inv) => 
      sum + parseCommission(inv['Commission (Points)']), 0
    );
    const avgCommission = totalCommission / filteredInvestments.length;
    
    const totalFactorRate = filteredInvestments.reduce((sum, inv) => 
      sum + (parseFloat(inv['Factor Rate']) || 0), 0
    );
    const avgFactorRate = totalFactorRate / filteredInvestments.length;
    
    return {
      term_percent: avgTermPercent.toFixed(2) + '%',
      rtr_paid: avgRTRPaid.toFixed(2) + '%',
      commission: avgCommission.toFixed(2) + '%',
      factor_rate: avgFactorRate.toFixed(2)
    };
  };

  const totals = calculateTotals();

  // Pagination calculations
  const totalRecords = sortedInvestments.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvestments = sortedInvestments.slice(startIndex, endIndex);
  const showingStart = totalRecords === 0 ? 0 : startIndex + 1;
  const showingEnd = Math.min(endIndex, totalRecords);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Indicator with Spinner */}
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Loading Investments...</h3>
            <p className="text-slate-500">Fetching your portfolio data</p>
          </div>
        </div>
        
        {/* Skeleton Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-48 mb-6"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <label className="text-sm text-slate-600 mb-1 block">Table Columns</label>
            <Select value={columnView} onValueChange={(value) => {
              setColumnView(value);
              const config = columnConfigs[value];
              toast.success(`Showing ${config.label}`);
            }}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default View (11 cols)</SelectItem>
                <SelectItem value="all">All Columns (33 cols)</SelectItem>
                <SelectItem value="financial">Financial Only (14 cols)</SelectItem>
                <SelectItem value="performance">Performance Only (13 cols)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-slate-600 mb-1 block">Items per page</label>
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="50" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="250">250</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900">{showingStart}-{showingEnd}</span> of <span className="font-semibold text-slate-900">{totalRecords}</span> investments
          </div>
          
          <Button
            onClick={downloadInvestments}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="w-4 h-4" />
            <span>Download Investments</span>
          </Button>
        </div>
      </div>

      {/* Status Filter Pills */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="text-sm text-slate-600 mb-3">Filter by Status</div>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => toggleStatus(status.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedStatuses.includes(status.value) || (status.value === 'all' && selectedStatuses.includes('all'))
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Investments Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase sticky left-0 bg-slate-50 z-10">#</th>
                {columnConfigs[columnView].columns.map((col) => (
                  <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase whitespace-nowrap">
                    <button
                      onClick={() => handleSort(col.key)}
                      className="flex items-center space-x-1 hover:text-slate-800 transition-colors"
                    >
                      <span>{col.label}</span>
                      {sortConfig.key === col.key ? (
                        sortConfig.direction === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronsUpDown className="w-4 h-4 opacity-50" />
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {/* Data Rows */}
              {currentInvestments.length > 0 ? (
                currentInvestments.map((investment, index) => {
                  const actualIndex = startIndex + index + 1;
                  return (
                    <tr 
                      key={index} 
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/investments/${investment['Investment ID']}`)}
                    >
                      <td className="px-4 py-3 text-sm text-slate-900 font-medium sticky left-0 bg-white hover:bg-slate-50 z-10">{actualIndex}</td>
                      {columnConfigs[columnView].columns.map((col) => (
                        <td 
                          key={col.key} 
                          className={`px-4 py-3 text-sm whitespace-nowrap ${
                            col.key === 'Investment ID' ? 'text-blue-600 font-medium' : 
                            col.key === 'Status' ? 'text-slate-700 font-medium' :
                            'text-slate-900'
                          }`}
                        >
                          {investment[col.key] || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnConfigs[columnView].columns.length + 1} className="px-4 py-12 text-center text-slate-500">
                    <p className="text-lg font-medium">No investments found</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalRecords > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{showingStart}</span> to <span className="font-semibold text-slate-900">{showingEnd}</span> of <span className="font-semibold text-slate-900">{totalRecords}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === 1
                    ? 'text-slate-400 bg-white cursor-not-allowed'
                    : 'text-slate-700 bg-white hover:bg-slate-100 border border-slate-300'
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-3 py-2 text-slate-600">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-700 bg-white hover:bg-slate-100 border border-slate-300'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === totalPages
                    ? 'text-slate-400 bg-white cursor-not-allowed'
                    : 'text-slate-700 bg-white hover:bg-slate-100 border border-slate-300'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Investments;
